import { Cron } from 'croner';

export type CountdownType = 'target' | 'recurring';

export type CountdownDisplayUnit =
  | 'auto'
  | 'minutes'
  | 'hours'
  | 'days'
  | 'weeks';

export interface CountdownConfig {
  type: CountdownType;
  name: string;
  targetAt: string;
  cron: string;
  displayUnit: CountdownDisplayUnit;
}

export interface CountdownEditValues {
  type: CountdownType;
  name: string;
  targetAt: string;
  cron: string;
  displayUnit: CountdownDisplayUnit;
}

export interface CountdownEditRequest {
  mode: 'edit' | 'insert';
  initialValues: CountdownEditValues;
  onCommit: (values: CountdownEditValues) => void | Promise<void>;
}

export interface FormattedCountdown {
  completed: boolean;
  primaryText: string;
  exactText: string | null;
}

const COUNTDOWN_DISPLAY_UNITS = new Set<CountdownDisplayUnit>([
  'auto',
  'minutes',
  'hours',
  'days',
  'weeks',
]);
const COUNTDOWN_TYPES = new Set<CountdownType>(['target', 'recurring']);

const MINUTE_MS = 60 * 1000;
const HOUR_MINUTES = 60;
const DAY_MINUTES = 24 * HOUR_MINUTES;
const WEEK_MINUTES = 7 * DAY_MINUTES;

function normalizeCountdownString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function normalizeCountdownType(value: unknown): CountdownType {
  return COUNTDOWN_TYPES.has(value as CountdownType)
    ? (value as CountdownType)
    : 'target';
}

export function normalizeCountdownDisplayUnit(
  value: unknown,
): CountdownDisplayUnit {
  return COUNTDOWN_DISPLAY_UNITS.has(value as CountdownDisplayUnit)
    ? (value as CountdownDisplayUnit)
    : 'auto';
}

export function normalizeCountdownConfig(input: unknown): CountdownConfig {
  const rec =
    input && typeof input === 'object'
      ? (input as Record<string, unknown>)
      : {};

  return {
    type: normalizeCountdownType(rec.type),
    name: normalizeCountdownString(rec.name),
    targetAt: normalizeCountdownString(rec.targetAt),
    cron: normalizeCountdownString(rec.cron),
    displayUnit: normalizeCountdownDisplayUnit(rec.displayUnit),
  };
}

export function countdownConfigToSerializable(
  data: CountdownConfig,
): Record<string, string> {
  const normalized = normalizeCountdownConfig(data);
  if (normalized.type === 'recurring') {
    return {
      type: 'recurring',
      name: normalized.name,
      cron: normalized.cron,
      displayUnit: normalized.displayUnit,
    };
  }

  return {
    type: 'target',
    name: normalized.name,
    targetAt: normalized.targetAt,
    displayUnit: normalized.displayUnit,
  };
}

export function stringifyCountdownConfig(data: CountdownConfig): string {
  return JSON.stringify(countdownConfigToSerializable(data), null, 2);
}

export function createDefaultCountdownEditValues(
  now = new Date(),
): CountdownEditValues {
  const next = new Date(now);
  next.setHours(next.getHours() + 1, 0, 0, 0);

  return {
    type: 'target',
    name: '',
    targetAt: next.toISOString(),
    cron: '',
    displayUnit: 'auto',
  };
}

function parseTargetTime(targetAt: string): number | null {
  const ts = Date.parse(targetAt);
  return Number.isFinite(ts) ? ts : null;
}

function parseRecurringCron(cronExpr: string): Cron | null {
  const trimmed = cronExpr.trim();
  if (!trimmed) {
    return null;
  }

  try {
    return new Cron(trimmed, { timezone: 'UTC' });
  } catch {
    return null;
  }
}

export function isValidRecurringCountdownCron(cronExpr: string): boolean {
  return parseRecurringCron(cronExpr) != null;
}

function formatCount(value: number, unit: string): string {
  return `${value} ${unit}${value === 1 ? '' : 's'}`;
}

function formatAutoCountdown(diffMinutes: number): string {
  if (diffMinutes < HOUR_MINUTES) {
    return formatCount(Math.max(1, diffMinutes), 'minute');
  }

  if (diffMinutes < DAY_MINUTES) {
    const hours = Math.floor(diffMinutes / HOUR_MINUTES);
    const minutes = diffMinutes % HOUR_MINUTES;
    return minutes > 0
      ? `${formatCount(hours, 'hour')} ${formatCount(minutes, 'minute')}`
      : formatCount(hours, 'hour');
  }

  if (diffMinutes < 14 * DAY_MINUTES) {
    const days = Math.floor(diffMinutes / DAY_MINUTES);
    const hours = Math.floor((diffMinutes % DAY_MINUTES) / HOUR_MINUTES);
    return hours > 0
      ? `${formatCount(days, 'day')} ${formatCount(hours, 'hour')}`
      : formatCount(days, 'day');
  }

  const weeks = Math.floor(diffMinutes / WEEK_MINUTES);
  const days = Math.floor((diffMinutes % WEEK_MINUTES) / DAY_MINUTES);
  return days > 0
    ? `${formatCount(weeks, 'week')} ${formatCount(days, 'day')}`
    : formatCount(weeks, 'week');
}

function formatForcedCountdown(
  diffMs: number,
  unit: Exclude<CountdownDisplayUnit, 'auto'>,
): string {
  const divisor =
    unit === 'minutes'
      ? MINUTE_MS
      : unit === 'hours'
        ? HOUR_MINUTES * MINUTE_MS
        : unit === 'days'
          ? DAY_MINUTES * MINUTE_MS
          : WEEK_MINUTES * MINUTE_MS;
  const amount = Math.max(1, Math.ceil(diffMs / divisor));
  return formatCount(amount, unit.slice(0, -1));
}

export function formatCountdown(
  data: CountdownConfig,
  now = new Date(),
): FormattedCountdown {
  const normalized = normalizeCountdownConfig(data);
  if (normalized.type === 'recurring') {
    const schedule = parseRecurringCron(normalized.cron);
    if (!schedule) {
      return {
        completed: false,
        primaryText: 'Invalid schedule',
        exactText: null,
      };
    }

    const nextRun = schedule.nextRun(now);
    if (nextRun == null) {
      return {
        completed: false,
        primaryText: 'Invalid schedule',
        exactText: null,
      };
    }

    const diffMs = Math.max(1, nextRun.getTime() - now.getTime());
    const diffMinutes = Math.max(1, Math.ceil(diffMs / MINUTE_MS));
    const primaryText =
      normalized.displayUnit === 'auto'
        ? formatAutoCountdown(diffMinutes)
        : formatForcedCountdown(diffMs, normalized.displayUnit);

    return {
      completed: false,
      primaryText,
      exactText: formatCountdownExactTime(nextRun.toISOString()),
    };
  }

  const targetTs = parseTargetTime(normalized.targetAt);
  if (targetTs == null) {
    return {
      completed: false,
      primaryText: 'Invalid date',
      exactText: null,
    };
  }

  const diffMs = targetTs - now.getTime();
  if (diffMs <= 0) {
    return {
      completed: true,
      primaryText: '✅',
      exactText: null,
    };
  }

  const diffMinutes = Math.max(1, Math.ceil(diffMs / MINUTE_MS));
  const primaryText =
    normalized.displayUnit === 'auto'
      ? formatAutoCountdown(diffMinutes)
      : formatForcedCountdown(diffMs, normalized.displayUnit);

  return {
    completed: false,
    primaryText,
    exactText: formatCountdownExactTime(normalized.targetAt),
  };
}

export function formatCountdownExactTime(targetAt: string): string | null {
  const targetTs = parseTargetTime(targetAt);
  if (targetTs == null) {
    return null;
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(targetTs));
}

export function countdownIsoToLocalInputValue(value: string): string {
  const targetTs = parseTargetTime(value);
  if (targetTs == null) {
    return '';
  }

  const local = new Date(targetTs);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(
    local.getDate(),
  )}T${pad(local.getHours())}:${pad(local.getMinutes())}`;
}

export function countdownLocalInputValueToIso(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = new Date(trimmed);
  return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : null;
}
